/**
 * HomeView — pantalla raíz `/`.
 *
 * Tras B.5.c.2 muestra el dashboard del workspace alineado al prototipo
 * v1: hero con saludo + bandeja personal + cards de proyectos + alertas
 * del tenant + stats. Datos hardcoded marcados con `// TODO: B.5.d` para
 * los componentes que aún no tienen endpoint:
 *
 *   - Bandeja personal      → GET /me/inbox        (B.5.d)
 *   - Alertas del tenant    → GET /tenants/{id}/alerts (B.5.d/+)
 *   - Stats del tenant      → GET /tenants/{id}/stats   (B.5.d)
 *
 * Lo que SÍ tiene endpoint real:
 *   - "Tus proyectos"       → GET /api/v1/projects (existe desde B.3)
 *
 * Estados especiales (limbo / disabled / sin tenant) NO ven el dashboard;
 * ven sus propias pantallas.
 *
 * El `external` tampoco (OT-0017): lo único que hace en la app es aprobar
 * los documentos donde es aprobador, así que su home es su bandeja de
 * aprobaciones (`/me/inbox`) y nada más — sin proyectos, alertas ni KPIs
 * que no puede abrir.
 */

import { useMemo } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { useTranslation } from 'react-i18next'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ChevronRight,
  Info,
  ShieldCheck,
} from 'lucide-react'

import { ApiError, isForbiddenTier } from '../api/client'
import { ScrollPage } from '../components/shell/ScrollPage'
import {
  fetchMyInbox,
  type InboxApprovalItem,
  type InboxResponse,
  type MeResponse,
} from '../api/me'
import {
  listProjects,
  type Project,
  type ProjectStatus,
} from '../api/projects'
import { fetchTenantStats, type TenantStatsResponse } from '../api/tenants'
import { LoginScreen } from '../auth/LoginScreen'
import { isMsalConfigured } from '../auth/msalConfig'
import { MeError, MsalNotConfigured } from '../auth/SessionScreens'
import { useAccessToken } from '../auth/useAccessToken'
import { useMe } from '../auth/useMe'
import {
  canReadTenantInternalResources,
  isExternal,
  type MaybeTier,
} from '../lib/tiers'
import { pickLocalized } from '../i18n/config'

export function HomeView() {
  if (!isMsalConfigured) {
    return <MsalNotConfigured />
  }
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <WorkspaceDashboardOrSpecial />
      </AuthenticatedTemplate>
    </>
  )
}

// ─── Switch entre dashboard normal y estados especiales ────────────────────
function WorkspaceDashboardOrSpecial() {
  const { t } = useTranslation(['auth', 'errors'])
  const { me, isLoading, error } = useMe()

  if (isLoading) {
    return (
      <ScrollPage>
        <p className="font-mono text-sm text-ink-subtle">
          {t('auth:me.loading')}
        </p>
      </ScrollPage>
    )
  }
  if (error) {
    return (
      <ScrollPage>
        <MeError error={error} />
      </ScrollPage>
    )
  }
  if (!me) return null

  // Estados especiales que NO ven el dashboard.
  if (me.status === 'pending' && me.tenant === null) {
    return (
      <ScrollPage>
        <LimboState me={me} />
      </ScrollPage>
    )
  }
  if (me.status === 'disabled') {
    return (
      <ScrollPage>
        <DisabledState me={me} />
      </ScrollPage>
    )
  }
  if (!me.tenant) {
    return (
      <ScrollPage>
        <UnknownState me={me} />
      </ScrollPage>
    )
  }

  if (isExternal(me.role)) {
    return (
      <ScrollPage>
        <ExternalHome me={me} />
      </ScrollPage>
    )
  }

  // Estado feliz: tenant activo + dashboard completo.
  return (
    <ScrollPage>
      <WorkspaceDashboard me={me} />
    </ScrollPage>
  )
}

// ─── Estados especiales ────────────────────────────────────────────────────
function LimboState({ me }: { me: MeResponse }) {
  const { t } = useTranslation(['workspace', 'auth'])
  return (
    <div className="rounded-md border border-review/40 bg-review-soft p-5 max-w-3xl">
      <h3 className="font-display text-base text-ink">
        {t('workspace:limbo.title')}
      </h3>
      <p className="mt-2 max-w-prose font-body text-sm text-ink-muted">
        {t('workspace:limbo.body', { email: me.email })}
      </p>
      <details className="mt-4">
        <summary className="cursor-pointer font-ui text-xs text-ink-subtle">
          {t('auth:me.limbo_details')}
        </summary>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 font-mono text-xs">
          <dt className="text-ink-subtle">{t('auth:me.fields.user_id')}</dt>
          <dd className="text-ink">{me.id}</dd>
          <dt className="text-ink-subtle">{t('auth:me.fields.entra_oid')}</dt>
          <dd className="text-ink">{me.entra_oid}</dd>
          <dt className="text-ink-subtle">{t('auth:me.fields.status')}</dt>
          <dd className="text-ink">{me.status}</dd>
        </dl>
      </details>
    </div>
  )
}

function DisabledState({ me }: { me: MeResponse }) {
  const { t } = useTranslation('auth')
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-5 max-w-3xl">
      <h3 className="font-display text-base text-critical">
        {t('me.disabled_title')}
      </h3>
      <p className="mt-2 font-body text-sm text-ink-muted">
        {t('me.disabled_body', { email: me.email })}
      </p>
    </div>
  )
}

function UnknownState({ me }: { me: MeResponse }) {
  const { t } = useTranslation('auth')
  return (
    <div className="rounded-md border border-line bg-surface-muted p-4 max-w-3xl">
      <p className="font-body text-sm text-ink-muted">
        {t('me.unknown_state', {
          status: me.status,
          tenant: me.tenant ? 'set' : 'null',
        })}
      </p>
      <pre className="mt-2 font-mono text-xs text-ink overflow-x-auto">
        {JSON.stringify(me, null, 2)}
      </pre>
    </div>
  )
}

// ─── Dashboard del workspace (estado feliz) ────────────────────────────────
/** Una sola query para /me/inbox que comparten Hero (summary counts) y la
 *  lista, en las dos homes. TanStack Query desduplica por key. */
function useInboxQuery(): InboxQuery {
  const { accessToken } = useAccessToken()
  return useQuery({
    queryKey: ['me', 'inbox'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchMyInbox(accessToken)
    },
    enabled: !!accessToken,
    staleTime: 30_000,
    retry: false,
  })
}

function WorkspaceDashboard({ me }: { me: MeResponse }) {
  const inboxQuery = useInboxQuery()

  return (
    <div className="max-w-7xl space-y-10">
      <Hero me={me} inboxQuery={inboxQuery} />
      <InboxSection inboxQuery={inboxQuery} role={me.role} />
      <MyProjectsSection />
      <AlertsAndStatsSection me={me} />
    </div>
  )
}

// ─── Home del external (OT-0017) ───────────────────────────────────────────
function ExternalHome({ me }: { me: MeResponse }) {
  const inboxQuery = useInboxQuery()

  return (
    <div className="max-w-3xl space-y-10">
      <Hero me={me} inboxQuery={inboxQuery} />
      <ExternalApprovalsSection inboxQuery={inboxQuery} role={me.role} />
    </div>
  )
}

/** Sus aprobaciones pendientes, sin las pestañas de la bandeja de miembros
 *  (cambios y borradores no existen para este tier). Cada fila abre
 *  `/approvals/$artifactId` — `InboxApprovalRow` decide el destino por tier. */
function ExternalApprovalsSection({
  inboxQuery,
  role,
}: {
  inboxQuery: InboxQuery
  role: MaybeTier
}) {
  const { t } = useTranslation(['workspace', 'common'])
  const approvals = inboxQuery.data?.approvals ?? []

  return (
    <section>
      <h2 className="mb-3 font-display text-lg text-ink">{t('external_home.title')}</h2>

      {inboxQuery.isPending ? (
        <p role="status" className="font-mono text-xs text-ink-subtle">
          {t('common:actions.loading')}
        </p>
      ) : inboxQuery.isError ? (
        <div className="space-y-3">
          <ErrorBox error={inboxQuery.error} />
          <button
            type="button"
            onClick={() => void inboxQuery.refetch()}
            disabled={inboxQuery.isFetching}
            className="rounded-md border border-line px-3 py-1.5 font-ui text-xs text-ink
                       transition-colors hover:bg-surface-muted disabled:opacity-50
                       focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {t('common:actions.retry')}
          </button>
        </div>
      ) : approvals.length === 0 ? (
        <div className="rounded-md border border-line bg-surface p-6">
          <p className="font-body text-sm text-ink">{t('external_home.empty_title')}</p>
          <p className="mt-1 font-body text-sm text-ink-muted">
            {t('external_home.empty_body')}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-md border border-line bg-surface">
          {approvals.map((item) => (
            <li key={item.approval_id}>
              <InboxApprovalRow item={item} role={role} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

type InboxQuery = UseQueryResult<InboxResponse, Error>

function Hero({
  me,
  inboxQuery,
}: {
  me: MeResponse
  inboxQuery: InboxQuery
}) {
  const { t } = useTranslation('workspace')

  const external = isExternal(me.role)
  const summary = useMemo(() => {
    const counts = inboxQuery.data?.counts
    // Para el `external` la bandeja es toda su home (OT-0017): sin datos
    // (cargando o con error) no hay resumen, porque decir "no tienes
    // pendientes" antes de saberlo sería falso.
    if (!counts && external) return null
    const a = counts?.approvals ?? 0
    // `criticalChanges` lo derivaremos del endpoint cuando exista (Fase E).
    // Por ahora `changes` viene en 0 desde el backend.
    const c = counts?.changes ?? 0
    if (a === 0 && c === 0) return t('hero.summary_zero')
    if (c > 0) {
      const key =
        c === 1 ? 'hero.summary_with_critical' : 'hero.summary_with_critical_many'
      return t(key, { approvalsCount: a, criticalCount: c })
    }
    if (a === 1) return t('hero.summary_one_approval', { approvalsCount: a })
    return t('hero.summary_many_approvals', { approvalsCount: a })
  }, [t, inboxQuery.data, external])

  return (
    <section>
      <p className="font-ui text-xs uppercase tracking-widest text-ink-subtle">
        {t('hero.eyebrow', {
          tenantName: me.tenant?.display_name ?? '—',
        })}
      </p>
      <h1 className="mt-2 font-display text-3xl text-ink">
        {t('hero.greeting', { name: me.display_name })}
      </h1>
      {summary ? (
        <p className="mt-2 font-body text-base text-ink-muted">{summary}</p>
      ) : null}
    </section>
  )
}

// ─── Inbox section (datos reales /me/inbox) ────────────────────────────────
type InboxTab = 'approvals' | 'changes' | 'drafts'

function InboxSection({
  inboxQuery,
  role,
}: {
  inboxQuery: InboxQuery
  role: MaybeTier
}) {
  const { t } = useTranslation(['workspace', 'errors'])
  const tabs: InboxTab[] = ['approvals', 'changes', 'drafts']

  // Active tab: 'approvals' por default. El switch real entre tabs llega
  // cuando 'changes' y 'drafts' tengan datos (Fase E + B.6).
  const activeTab: InboxTab = 'approvals'

  const counts = inboxQuery.data?.counts ?? {
    approvals: 0,
    changes: 0,
    drafts: 0,
  }
  const approvals = inboxQuery.data?.approvals ?? []
  const isLoading = inboxQuery.isLoading
  const error = inboxQuery.error

  return (
    <section>
      <header className="flex items-end justify-between mb-3 gap-3">
        <h2 className="font-display text-lg text-ink">{t('inbox.title')}</h2>
        <nav className="flex items-center gap-1 text-xs">
          {tabs.map((tab) => {
            const active = tab === activeTab
            const count =
              tab === 'approvals'
                ? counts.approvals
                : tab === 'changes'
                  ? counts.changes
                  : counts.drafts
            return (
              <button
                key={tab}
                type="button"
                disabled={tab !== 'approvals'}
                className={
                  active
                    ? 'px-2.5 py-1 rounded-md bg-brand-soft text-brand font-medium'
                    : 'px-2.5 py-1 rounded-md text-ink-muted disabled:cursor-default'
                }
              >
                {t(`inbox.tabs.${tab}` as const)}{' '}
                <span className="font-mono">{count}</span>
              </button>
            )
          })}
        </nav>
      </header>

      <div className="rounded-md border border-line bg-surface overflow-hidden">
        {isLoading ? (
          <p className="px-5 py-8 text-center font-mono text-xs text-ink-subtle">
            …
          </p>
        ) : error ? (
          <div className="px-5 py-4">
            <ErrorBox error={error} />
          </div>
        ) : approvals.length === 0 ? (
          <p className="px-5 py-8 text-center font-body text-sm text-ink-muted">
            {t('inbox.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {approvals.map((item) => (
              <li key={item.approval_id}>
                <InboxApprovalRow item={item} role={role} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function InboxApprovalRow({
  item,
  role,
}: {
  item: InboxApprovalItem
  role: MaybeTier
}) {
  const { t, i18n } = useTranslation('workspace')
  // Doc 21 — Approval Workflow: si la approval pertenece a un workflow
  // multi-stage, pintamos el nombre del stage como chip. Para legacy
  // single-stage (`stage_order` null o 0 sin stage_name), no se muestra.
  const stageLabel: string | null = pickLocalized(item.stage_name, i18n.language) ?? null
  const stageChip = stageLabel ? (
    <span className="px-1.5 py-0.5 rounded bg-brand-soft text-[10px] font-sans text-brand">
      {t('inbox.stage_chip', {
        order: item.stage_order ?? 0,
        name: stageLabel,
      })}
    </span>
  ) : null

  // OT-0017 — al `external` no se le muestran el código ni el rol: la bandeja
  // trae la clave del catálogo (`rol-client-sponsor`) y, sin código, el
  // `template_key`, sin etiqueta resuelta, y este tier no lee el catálogo de
  // roles. La etapa sí viene localizada.
  const meta = isExternal(role) ? (
    stageChip ? (
      <p className="text-xs flex items-center gap-1.5">{stageChip}</p>
    ) : null
  ) : (
    <p className="text-xs text-ink-muted truncate font-mono flex items-center gap-1.5">
      <span>{item.artifact_code ?? item.template_key}</span>
      <span className="text-ink-subtle">·</span>
      <span>{item.approver_role}</span>
      {stageChip ? (
        <>
          <span className="text-ink-subtle">·</span>
          {stageChip}
        </>
      ) : null}
    </p>
  )

  const content = (
    <>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-review-soft text-review shrink-0">
        {t('inbox.status.in_review')}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate text-ink">
          {item.artifact_name}
          <span className="ml-2 font-mono text-xs text-ink-subtle">
            v{item.version_number}
          </span>
        </p>
        {meta}
      </div>
      <ChevronRight className="w-4 h-4 text-ink-subtle shrink-0" />
    </>
  )
  const className =
    'w-full flex items-center gap-4 px-5 py-3.5 hover:bg-surface-muted text-left transition-colors'

  // OT-0013 — el `external` no puede abrir la ruta de proyecto
  // (`can_view_project` lo niega por diseño, Doc 29 §4.6); su pendiente
  // vive en `/approvals/$artifactId`, artifact-scoped. Miembros y
  // admins siguen yendo a la ruta de proyecto — su experiencia no
  // cambia.
  if (isExternal(role)) {
    return (
      <Link
        to="/approvals/$artifactId"
        params={{ artifactId: item.artifact_id }}
        className={className}
      >
        {content}
      </Link>
    )
  }
  return (
    <Link
      to="/projects/$projectId/official/$artifactId"
      params={{
        projectId: item.project_id,
        artifactId: item.artifact_id,
      }}
      className={className}
    >
      {content}
    </Link>
  )
}

// ─── My projects section (datos reales) ─────────────────────────────────────
function MyProjectsSection() {
  const { t } = useTranslation(['workspace', 'errors'])
  const { accessToken, loading: authLoading } = useAccessToken()

  const query = useQuery({
    queryKey: ['projects', 'list'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listProjects(accessToken)
    },
    enabled: !!accessToken,
  })

  const projects = query.data ?? []
  const loading = authLoading || query.isLoading

  return (
    <section>
      <header className="flex items-end justify-between mb-3 gap-3">
        <h2 className="font-display text-lg text-ink">{t('projects.title')}</h2>
        <Link
          to="/projects"
          className="text-xs text-brand hover:underline"
        >
          {t('projects.see_all')}
        </Link>
      </header>

      {loading ? (
        <p className="font-mono text-xs text-ink-subtle">
          {t('projects.loading')}
        </p>
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : projects.length === 0 ? (
        <EmptyProjects />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </section>
  )
}

function ProjectCard({ project }: { project: Project }) {
  const { t } = useTranslation('workspace')
  const statusBadgeClass = projectStatusClass(project.status)

  return (
    <Link
      to="/projects/$projectId/overview"
      params={{ projectId: project.id }}
      className="block text-left bg-surface border border-line rounded-md p-5 hover:border-brand transition-colors"
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusBadgeClass}`}
        >
          {t(`projects.status.${project.status}` as const)}
        </span>
        <span className="text-[10px] font-mono text-ink-subtle">
          {project.code}
        </span>
      </div>
      <h3 className="font-display text-base text-ink">{project.name}</h3>
      <p className="mt-1 text-xs text-ink-muted truncate">
        {project.client_name
          ? `${t('projects.client_label')}: ${project.client_name} · `
          : ''}
        {t(`projects.methodology.${project.methodology}` as const)}
      </p>
      <p className="mt-3 text-[11px] text-ink-subtle">
        {t('projects.phase_placeholder')}
      </p>
    </Link>
  )
}

function projectStatusClass(status: ProjectStatus): string {
  switch (status) {
    case 'active':
      return 'bg-official-soft text-official'
    case 'on_hold':
      return 'bg-review-soft text-review'
    case 'closed':
      return 'bg-surface-muted text-ink-subtle'
    case 'cancelled':
      return 'bg-critical-soft text-critical'
    case 'draft':
    default:
      return 'bg-surface-muted text-ink-muted'
  }
}

function EmptyProjects() {
  const { t } = useTranslation('workspace')
  return (
    <div className="rounded-md border border-dashed border-line bg-surface p-8 text-center">
      <h3 className="font-display text-base text-ink">
        {t('projects.empty_title')}
      </h3>
      <p className="mt-2 max-w-prose mx-auto font-body text-sm text-ink-muted">
        {t('projects.empty_body')}
      </p>
    </div>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['workspace', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as const, { defaultValue: fallback })}
    </div>
  )
}

// ─── Alerts + stats ────────────────────────────────────────────────────────
// Los KPIs del tenant son un recurso interno (OT-0015): sin permiso no se
// monta el bloque ni se consulta `/stats`, y si el backend niega la lectura
// (`FORBIDDEN_TIER`) el bloque desaparece. En ambos casos las alertas ocupan
// todo el ancho en vez de dejar una columna vacía.
function AlertsAndStatsSection({ me }: { me: MeResponse }) {
  if (!canReadTenantInternalResources(me.role)) return <AlertsOnly />
  return <AlertsWithTenantStats me={me} />
}

function AlertsOnly() {
  return (
    <section className="grid grid-cols-1 gap-4">
      <TenantAlerts />
    </section>
  )
}

function AlertsWithTenantStats({ me }: { me: MeResponse }) {
  const { accessToken } = useAccessToken()
  const tenantId = me.tenant?.id

  const query = useQuery({
    queryKey: ['tenants', tenantId, 'stats'],
    queryFn: () => {
      if (!accessToken || !tenantId) throw new Error('preconditions')
      return fetchTenantStats(accessToken, tenantId)
    },
    enabled: !!accessToken && !!tenantId,
    staleTime: 30_000,
    retry: false,
  })

  if (isForbiddenTier(query.error)) return <AlertsOnly />
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <TenantAlerts className="lg:col-span-2" />
      <TenantStats query={query} />
    </section>
  )
}

function TenantAlerts({ className }: { className?: string }) {
  const { t } = useTranslation('workspace')

  // TODO: B.5.d/+ — reemplazar por GET /tenants/{id}/alerts.
  const placeholderAlerts: Array<{
    icon: 'warn' | 'info' | 'shield'
    title: string
    body: string
    actionable?: boolean
  }> = []

  return (
    <div className={`${className ?? ''} bg-surface border border-line rounded-md p-5`}>
      <h2 className="font-display text-lg text-ink mb-3">{t('alerts.title')}</h2>
      {placeholderAlerts.length === 0 ? (
        <p className="font-body text-sm text-ink-muted">{t('alerts.empty')}</p>
      ) : (
        <ul className="divide-y divide-line">
          {placeholderAlerts.map((alert, idx) => (
            <li key={idx} className="py-3 flex items-start gap-3">
              {alert.icon === 'warn' ? (
                <AlertTriangle className="w-4 h-4 text-high mt-0.5 shrink-0" />
              ) : alert.icon === 'shield' ? (
                <ShieldCheck className="w-4 h-4 text-official mt-0.5 shrink-0" />
              ) : (
                <Info className="w-4 h-4 text-brand mt-0.5 shrink-0" />
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-ink">{alert.title}</p>
                <p className="text-xs text-ink-muted">{alert.body}</p>
              </div>
              {alert.actionable ? (
                <button
                  type="button"
                  disabled
                  className="text-xs text-brand disabled:opacity-50 disabled:cursor-default"
                >
                  {t('alerts.see_more')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function TenantStats({ query }: { query: UseQueryResult<TenantStatsResponse, Error> }) {
  const { t } = useTranslation(['workspace', 'errors'])

  // Display: si carga o no hay datos aún, mostramos "—"; si error, error box;
  // si ok, números reales. Doc 19 §3.2 (errores localizados via humanizeError).
  // Adapter: API field `canon_published` → UI key `official_published`.
  const stats: {
    active_projects: string | number
    official_published: string | number
    open_crs: string | number
    members: string | number
  } = query.data
    ? {
        active_projects: query.data.active_projects,
        official_published: query.data.canon_published,
        open_crs: query.data.open_crs,
        members: query.data.members,
      }
    : {
        active_projects: '—',
        official_published: '—',
        open_crs: '—',
        members: '—',
      }

  return (
    <div className="bg-surface border border-line rounded-md p-5">
      <h2 className="font-display text-lg text-ink mb-3">
        {t('workspace:stats.title')}
      </h2>
      {query.error ? (
        <ErrorBox error={query.error} />
      ) : (
        <dl className="space-y-3">
          <StatRow
            label={t('workspace:stats.active_projects')}
            value={stats.active_projects}
          />
          <StatRow
            label={t('workspace:stats.official_published')}
            value={stats.official_published}
          />
          <StatRow
            label={t('workspace:stats.open_crs')}
            value={stats.open_crs}
            accent="high"
          />
          <StatRow
            label={t('workspace:stats.members')}
            value={stats.members}
          />
        </dl>
      )}
    </div>
  )
}

function StatRow({
  label,
  value,
  accent,
}: {
  label: string
  value: string | number
  accent?: 'high' | undefined
}) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd
        className={
          accent === 'high'
            ? 'font-display text-2xl text-high'
            : 'font-display text-2xl text-ink'
        }
      >
        {value}
      </dd>
    </div>
  )
}
